<template>
    <div
        v-if="hydrated"
        ref="wrapper"
        :style="{ display: 'contents' }"
    >
        <slot></slot>
    </div>
    <div
        v-else
        ref="wrapper"
    ></div>
</template>

<script setup lang="ts">
import type { PropType, Ref } from 'vue'
import { onMounted, ref, watch } from 'vue'
import { getTimeout, getWindow } from '@/helpers/globals'

type VoidFunction = () => void

const isBrowser = () => {
    const window = getWindow()
    return typeof window === 'object'
}

const props = defineProps({
    ssrOnly: Boolean,
    whenIdle: Boolean,
    whenIdleTimeout: {
        type: Number,
        required: false,
        default: () => 500,
    },
    timeout: {
        type: Number,
        required: false,
        default: undefined,
    },
    whenVisible: {
        type: [Boolean, Object] as PropType<boolean | IntersectionObserverInit>,
        required: false,
        default: false,
    },
    promise: {
        type: Object as PropType<Promise<any>>,
        required: false,
        default: undefined,
    },
    on: {
        type: [Array, String] as PropType<
            (keyof HTMLElementEventMap)[] | keyof HTMLElementEventMap
        >,
        required: false,
        default: undefined,
    },
    didHydrate: {
        type: Function as PropType<() => void>,
        required: false,
        default: undefined,
    },
    noSsr: Boolean,
})

const noOptions =
    !props.ssrOnly &&
    !props.whenIdle &&
    !props.timeout &&
    !props.whenVisible &&
    !props.on?.length &&
    !props.promise
const wrapper: Ref<Element | null> = ref(null)
const hydrated = ref(noOptions || (!props.noSsr && !isBrowser()))
const hydrate = () => {
    hydrated.value = true
}

onMounted(() => {
    if (wrapper.value && noOptions && !wrapper.value.hasChildNodes()) {
        hydrate()
    }
})

watch(
    hydrated,
    (hydrate) => {
        if (hydrate && props.didHydrate) {
            props.didHydrate()
        }
    },
    { immediate: true }
)

watch(
    [() => props, wrapper, hydrated],
    (
        [
            { on, promise, ssrOnly, whenIdle, timeout, whenVisible },
            wrapper,
            hydrated,
        ],
        _,
        onInvalidate
    ) => {
        if (ssrOnly || hydrated) {
            return
        }

        const cleanupFns: VoidFunction[] = []
        const cleanup = () => {
            cleanupFns.forEach((fn) => {
                fn()
            })
        }

        if (promise) {
            promise.then(hydrate, hydrate)
        }

        if (whenVisible) {
            if (wrapper && typeof IntersectionObserver !== 'undefined') {
                const observerOptions: IntersectionObserverInit =
                    typeof whenVisible === 'object'
                        ? whenVisible
                        : {
                              rootMargin: '250px',
                          }

                const io = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (
                            entry.isIntersecting ||
                            entry.intersectionRatio > 0
                        ) {
                            hydrate()
                        }
                    })
                }, observerOptions)

                io.observe(wrapper)

                cleanupFns.push(() => {
                    io.disconnect()
                })
            } else {
                return hydrate()
            }
        }

        if (whenIdle) {
            const window = getWindow()
            if (window && typeof window.requestIdleCallback !== 'undefined') {
                const idleCallbackId = window.requestIdleCallback(hydrate, {
                    timeout: props.whenIdleTimeout,
                })
                cleanupFns.push(() => {
                    window.cancelIdleCallback(idleCallbackId)
                })
            } else {
                const theTimeout = getTimeout()
                if (theTimeout) {
                    const { setTimeout, clearTimeout } = theTimeout
                    const id = setTimeout(hydrate, 1500 + props.whenIdleTimeout)
                    cleanupFns.push(() => {
                        clearTimeout(id)
                    })
                }
            }
        }

        if (timeout && !import.meta.env.SSR) {
            const theTimeout = getTimeout()
            if (theTimeout) {
                const { setTimeout, clearTimeout } = theTimeout

                const id = setTimeout(hydrate, timeout)
                cleanupFns.push(() => {
                    clearTimeout(id)
                })
            }
        }

        if (on) {
            const events = ([] as Array<keyof HTMLElementEventMap>).concat(on)

            events.forEach((event) => {
                wrapper?.addEventListener(event, hydrate, {
                    once: true,
                    passive: true,
                })
                cleanupFns.push(() => {
                    wrapper?.removeEventListener(event, hydrate, {})
                })
            })
        }

        onInvalidate(cleanup)
    },
    { immediate: true }
)
</script>
